/* eslint-disable no-bitwise */
import React, { PureComponent } from 'react';
import { Loader, Button } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import deepEqual from 'deep-equal';
import * as classes from './classes.module.scss';
import Constants from '../../config/const';

const MARK_PADDING_PX = 4;
const DEFAULT_MARK_COLOR = 'red';
const CONNECTOR_MARK_COLOR = '#f28c8c';

const lightenDarkenColor = (col, amt) => {
  let usePound = false;
  if (col[0] === '#') {
    // eslint-disable-next-line no-param-reassign
    col = col.slice(1);
    usePound = true;
  }

  const num = parseInt(col, 16);

  let r = (num >> 16) + amt;

  if (r > 255) r = 255;
  else if (r < 0) r = 0;

  let b = ((num >> 8) & 0x00FF) + amt;

  if (b > 255) b = 255;
  else if (b < 0) b = 0;

  let g = (num & 0x0000FF) + amt;

  if (g > 255) g = 255;
  else if (g < 0) g = 0;

  let v = (g | (b << 8) | (r << 16)).toString(16);
  if (v.length < 6) {
    v = '0'.repeat(6 - v.length) + v;
  }
  return (usePound ? '#' : '') + v;
};

const fillBackrround = (ctx, color, canvasWidth, canvasHeight) => {
  const cleanColor = color.length > 5 ? color : (`#${ color.replace(/#/, '')  }${ color.replace(/#/, '') }`);
  const gradColor = cleanColor === '#ffffff' ? '#f0f0ef' : lightenDarkenColor(cleanColor, -30);
  const gradient = ctx.createLinearGradient(0, 0, canvasWidth, canvasHeight);
  gradient.addColorStop(0, cleanColor);
  gradient.addColorStop(1, gradColor);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);
};

const renderCaption = (ctx, text, x, y, align) => {
  ctx.beginPath();
  ctx.textAlign = align;
  ctx.font = 'bold 12px sans-serif';
  ctx.fillStyle = DEFAULT_MARK_COLOR;
  ctx.fillText(`${ text.toFixed(1) }"`, x, y);
  ctx.closePath();
};

const renderMark = (x0, x1, y0, y1, ctx, color = DEFAULT_MARK_COLOR) => {
  ctx.beginPath();
  ctx.strokeStyle = color;
  ctx.moveTo(x0, y0);
  ctx.lineTo(x1, y1);
  ctx.stroke();
  ctx.closePath();
};

const fullyContained = (iX, iY, iW, iH, canvasWidth, canvasHeight) => (
  iX >= 0 && iY >= 0 && iX + iW <= canvasWidth && iY + iH <= canvasHeight
);

const cropImage = (iX, iY, iW, iH, canvasWidth, canvasHeight) => {
  let [sx, sy, sw, sh, dx, dy, dw, dh] = [iX, iY, iW, iH, iX, iY, iW, iH];
  if (iX < 0) {
    dx = 0;
    sx = -iX;
    sw -= -iX;
    dw -= -iX;
  } else {
    sx = 0;
  }
  if (iY < 0) {
    dy = 0;
    sy = -iY;
    sh -= -iY;
    dh -= -iY;
  } else {
    sy = 0;
  }
  if (dx + dw > canvasWidth) {
    dw = canvasWidth - dx;
    sw = dw;
  }
  if (dy + dh > canvasHeight) {
    dh = canvasHeight - dy;
    sh = dh;
  }
  return [sx, sy, sw, sh, dx, dy, dw, dh];
};

const resizeImage = (src, width, height) => new Promise((resolve) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  const img = new Image();
  img.onload = () => {
    ctx.drawImage(img, 0, 0, width, height);
    resolve(ctx.canvas.toDataURL());
  };
  img.crossOrigin = '';
  img.src = src.match(/^http/) ? `${ src }?_=${ new Date().getTime() }` : src;
});

const addWallPaper = (
  ctx, src, widthIn, heightIn, dpi, previewX, previewY, previewWidth, previewHeight
) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    let x = previewX;
    // TODO: cut last preview image
    while (x < previewX + previewWidth) {
      let y = previewY;
      while (y < previewY + previewHeight) {
        ctx.drawImage(
          img,
          0, 0, widthIn * dpi, heightIn * dpi,
          (x - previewX) * dpi, (y - previewY) * dpi, widthIn * dpi, heightIn * dpi
        );
        y += heightIn;
      }
      x += widthIn;
    }
    resolve();
  };
  img.crossOrigin = '';
  img.src = src.match(/^http/) ? `${ src }?_=${ new Date().getTime() }` : src;
});

export default class Preview extends PureComponent {
  static propTypes = {
    worksExtended: PropTypes.array.isRequired,
    wallPaper: PropTypes.shape({
      x: PropTypes.number,
      y: PropTypes.number,
      src: PropTypes.string
    }),
    previewConfig: PropTypes.shape({
      height: PropTypes.number,
      width: PropTypes.number,
      x: PropTypes.number,
      y: PropTypes.number
    }),
    hangingVariations: PropTypes.arrayOf(PropTypes.shape({
      i0: PropTypes.number.isRequired,
      i1: PropTypes.number.isRequired,
      kinds: PropTypes.arrayOf(PropTypes.string)
    })),
    markPositions: PropTypes.shape({
      top: PropTypes.number.isRequired,
      left: PropTypes.number.isRequired,
      bottom: PropTypes.number.isRequired,
      right: PropTypes.number.isRequired,
      groundLeft: PropTypes.number.isRequired
    }),
    onBuildStarted: PropTypes.func.isRequired,
    onBuildComplete: PropTypes.func.isRequired,
    onClose: PropTypes.func,
    currentColor: PropTypes.string.isRequired,
    customImage: PropTypes.object,
    onlyPlaceHolders: PropTypes.bool,
    display: PropTypes.bool.isRequired,
    wallHeight: PropTypes.number
  };

  canvas = React.createRef();

  constructor(props) {
    super(props);
    this.state = { previewSrc: null, building: false };
  }

  componentDidMount() {
    this.renderPreview();
  }

  componentDidUpdate(prevProps) {
    const {
      previewConfig,
      worksExtended,
      currentColor,
      onlyPlaceHolders,
      wallPaper,
      customImage
    } = this.props;
    const {
      previewConfig: previewConfigWas,
      worksExtended: worksExtendedWas,
      currentColor: currentColorWas,
      onlyPlaceHolders: onlyPlaceHoldersWas,
      wallPaper: wallPaperWas,
      customImage: customImageWas
    } = prevProps;
    const compareProps = {
      previewConfig,
      worksExtended,
      currentColor,
      onlyPlaceHolders,
      wallPaper,
      customImage
    };
    const comparePropsWas = {
      previewConfig: previewConfigWas,
      worksExtended: worksExtendedWas,
      currentColor: currentColorWas,
      onlyPlaceHolders: onlyPlaceHoldersWas,
      wallPaper: wallPaperWas,
      customImage: customImageWas
    };
    if (!deepEqual(compareProps, comparePropsWas)) {
      this.renderPreview();
    }
  }

  onBuildComplete = (src, previewReady) => {
    const { onBuildComplete } = this.props;
    this.setState({ building: !previewReady, previewSrc: src });
    onBuildComplete(src, previewReady);
  };

  onBuildStarted = () => {
    const { onBuildStarted } = this.props;
    onBuildStarted();
    this.setState({ building: true, previewSrc: null });
  }

  onImageRendered(imagesLoaded, totalImages, ctx, dpi) {
    const { onlyPlaceHolders } = this.props;
    const res = imagesLoaded + 1;
    if (res === totalImages && this.canvas.current) {
      if (onlyPlaceHolders) {
        this.renderMarks(ctx, dpi);
      }
      this.onBuildComplete(this.canvas.current.toDataURL(), true);
    }
    return res;
  }

  addImage(shiftX, shiftY, previewX, previewY, x, y, previewW, previewH,
    url, dpi, ctx, canvasWidth, canvasHeight, skipShadow, onlyPlaceHolders, imageNumber) {
    return new Promise((resolve) => {
      const iX = shiftX + (parseFloat(x) - previewX) * dpi;
      const iY = shiftY + (parseFloat(y) - previewY) * dpi;
      const iW = previewW * dpi;
      const iH = previewH * dpi;
      resizeImage(url.match(/^http/) ? `${ url }?_=${ new Date().getTime() }` : url, iW, iH).then((src) => {
        const img = new Image();
        img.onload = () => {
          ctx.beginPath();
          if (!skipShadow && !onlyPlaceHolders) {
            ctx.shadowOffsetX = 4;
            ctx.shadowOffsetY = 6;
            ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
            ctx.shadowBlur = 19;
          }
          if (fullyContained(iX, iY, iW, iH, canvasWidth, canvasHeight)) {
            if (onlyPlaceHolders) {
              ctx.fillStyle = '#adadad';
              ctx.fillRect(iX, iY, iW, iH);
              const text = `${ imageNumber + 1 }`;
              const numbersSize = dpi * 5;
              ctx.font = `bold ${ numbersSize }px sans-serif`;
              const dx = parseInt(iW / 2, 10);
              ctx.textBaseline = 'middle';
              ctx.textAlign = 'center';
              ctx.fillStyle = '#fff';
              ctx.fillText(text, iX + dx, iY + parseInt(iH / 2, 10));
              this.positions[imageNumber] = {
                x: iX, y: iY, w: iW, h: iH
              };
            } else {
              ctx.drawImage(img, iX, iY, iW, iH);
            }
          } else {
            const [sx, sy, sw, sh, dx, dy, dw, dh] = cropImage(
              iX, iY, iW, iH, canvasWidth, canvasHeight
            );
            ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
          }
          ctx.closePath();
          resolve();
        };
        img.crossOrigin = '';
        img.src = src;
      });
    });
  }

  renderGroundMark(ctx) {
    const { wallHeight, worksExtended, markPositions: { groundLeft } } = this.props;
    const { height: canvasHeight } = Constants.preview;
    const lowerPos = canvasHeight - MARK_PADDING_PX * 2;
    const mappedWorks = worksExtended.map((w, index) => ({
      x: parseFloat(w.x),
      index
    })).sort((a, b) => Math.sign(a.x - b.x));
    const { index } = mappedWorks[0];
    const leftestWork = worksExtended[index];
    const text = parseFloat((wallHeight || 138) - leftestWork.y);
    const { x, y } = this.positions[index];
    renderMark(groundLeft, groundLeft, y, lowerPos, ctx);
    const mx0 = groundLeft - MARK_PADDING_PX;
    const mx1 = groundLeft + MARK_PADDING_PX;
    renderMark(mx0, mx1, y, y, ctx);
    renderMark(mx0, mx1, lowerPos, lowerPos, ctx);
    renderMark(groundLeft, x, y, y, ctx, CONNECTOR_MARK_COLOR);
    renderCaption(ctx, text, groundLeft - MARK_PADDING_PX, y + (lowerPos - y) / 2, 'right');
  }

  renderHangingMark(ctx, dpi, index0, index1, kind) {
    const { markPositions } = this.props;
    let x0 = 0;
    let y0 = 0;
    let x1 = 0;
    let y1 = 0;
    // eslint-disable-next-line no-unused-vars
    let _;
    if (kind === 'top' || kind === 'bottom') {
      y0 = markPositions[kind] * dpi;
      const pos = [
        this.positions[index0].x,
        this.positions[index0].x + this.positions[index0].w,
        this.positions[index1].x,
        this.positions[index1].x + this.positions[index1].w
      ].sort((a, b) => Math.sign(a - b));
      [_, x0, x1, _] = pos;
      renderMark(x0, x1, y0, y0, ctx);
      renderMark(x0, x0, y0 - MARK_PADDING_PX, y0 + MARK_PADDING_PX, ctx);
      renderMark(x1, x1, y0 - MARK_PADDING_PX, y0 + MARK_PADDING_PX, ctx);
      const imageMarkerBottom = (kind === 'top')
        ? Math.max(this.positions[index0].y, this.positions[index1].y)
        : Math.min(
          this.positions[index0].y + this.positions[index0].h,
          this.positions[index1].y + this.positions[index1].h
        );
      renderMark(x0, x0, y0, imageMarkerBottom, ctx, CONNECTOR_MARK_COLOR);
      renderMark(x1, x1, y0, imageMarkerBottom, ctx, CONNECTOR_MARK_COLOR);
      renderCaption(ctx, Math.abs((x1 - x0)) / dpi, x0 + ((x1 - x0) / 2), y0 - 2 * MARK_PADDING_PX, 'center');
    } else {
      x0 = markPositions[kind] * dpi;
      const pos = [
        this.positions[index0].y,
        this.positions[index0].y + this.positions[index0].h,
        this.positions[index1].y,
        this.positions[index1].y + this.positions[index1].h
      ].sort((a, b) => Math.sign(a - b));
      // eslint-disable-next-line no-unused-vars
      [_, y0, y1, _] = pos;
      renderMark(x0, x0, y0, y1, ctx);
      renderMark(x0 - MARK_PADDING_PX, x0 + MARK_PADDING_PX, y0, y0, ctx);
      renderMark(x0 - MARK_PADDING_PX, x0 + MARK_PADDING_PX, y1, y1, ctx);
      const imageMarkerSide = (kind === 'left')
        ? Math.max(this.positions[index0].x, this.positions[index1].x)
        : Math.min(
          this.positions[index0].x + this.positions[index0].w,
          this.positions[index1].x + this.positions[index1].w
        );
      renderMark(imageMarkerSide, x0, y0, y0, ctx, CONNECTOR_MARK_COLOR);
      renderMark(imageMarkerSide, x0, y1, y1, ctx, CONNECTOR_MARK_COLOR);
      renderCaption(
        ctx,
        Math.abs(y1 - y0) / dpi,
        x0 + MARK_PADDING_PX * (kind === 'left' ? -1 : 1),
        y0 + (y1 - y0) / 2,
        kind === 'left' ? 'right' : 'left'
      );
    }
  }

  renderMarks(ctx, dpi) {
    const { hangingVariations } = this.props;
    hangingVariations.forEach((v) => {
      v.kinds.forEach((kind) => {
        this.renderHangingMark(ctx, dpi, v.i0, v.i1, kind);
      });
    });
    this.renderGroundMark(ctx);
  }

  renderWorks(shiftX, shiftY, dpi, ctx, imagesLoaded, totalImages) {
    let iLoaded = imagesLoaded;
    const { previewConfig, worksExtended, onlyPlaceHolders } = this.props;
    const { x: previewX, y: previewY } = previewConfig;
    const { width: canvasWidth, height: canvasHeight } = Constants.preview;

    worksExtended.forEach((w, index) => {
      const {
        zoomPreviewUrl, x, y, previewW, previewH
      } = w;
      this.addImage(
        shiftX, shiftY, previewX, previewY, x, y, previewW, previewH,
        zoomPreviewUrl, dpi, ctx, canvasWidth, canvasHeight, false,
        onlyPlaceHolders, index
      ).then(() => {
        iLoaded = this.onImageRendered(iLoaded, totalImages, ctx, dpi);
      });
    });
  }

  renderWallPaper(shiftX, shiftY, dpi, ctx, il, ti) {
    let totalImages = ti;
    let imagesLoaded = il;
    const {
      previewConfig: {
        width: previewWidth, height: previewHeight, x: previewX, y: previewY
      },
      wallPaper
    } = this.props;
    const { src, widthIn, heightIn } = wallPaper || {};
    if (src) {
      totalImages += 1;
      resizeImage(src, widthIn * dpi, heightIn * dpi).then((newSrc) => {
        addWallPaper(ctx, newSrc, widthIn, heightIn, dpi,
          previewX, previewY, previewWidth, previewHeight).then(() => {
          imagesLoaded = this.onImageRendered(imagesLoaded, totalImages, ctx, dpi);
          this.renderCustomImage(shiftX, shiftY, dpi, ctx, imagesLoaded, totalImages);
        });
      });
    } else {
      this.renderCustomImage(shiftX, shiftY, dpi, ctx, imagesLoaded, totalImages);
    }
  }

  renderCustomImage(shiftX, shiftY, dpi, ctx, il, ti) {
    let totalImages = ti;
    let imagesLoaded = il;
    const { previewConfig, customImage, onlyPlaceHolders }  = this.props;
    const { x: previewX, y: previewY } = previewConfig;
    const { width: canvasWidth, height: canvasHeight } = Constants.preview;
    if (customImage && customImage.src) {
      totalImages += 1;
      const {
        widthIn, heightIn, posX, posY, src
      } = customImage;
      resizeImage(src, widthIn * dpi, heightIn * dpi).then((newSrc) => {
        this.addImage(
          shiftX, shiftY, previewX, previewY, posX, posY, widthIn, heightIn,
          newSrc, dpi, ctx, canvasWidth, canvasHeight, true,
          onlyPlaceHolders, -1
        ).then(() => {
          imagesLoaded = this.onImageRendered(imagesLoaded, totalImages, ctx, dpi);
          this.renderWorks(shiftX, shiftY, dpi, ctx, imagesLoaded, totalImages);
        });
      });
    } else {
      this.renderWorks(shiftX, shiftY, dpi, ctx, imagesLoaded, totalImages);
    }
  }

  renderPreview() {
    const { building } = this.state;
    if (building) { return; }

    const {
      previewConfig, worksExtended, currentColor, onlyPlaceHolders
    } = this.props;
    const { width: canvasWidth, height: canvasHeight } = Constants.preview;
    this.onBuildStarted();
    this.positions = [];
    const {
      width: previewWidth, height: previewHeight
    } = previewConfig;

    if (!canvasWidth || !worksExtended.length || worksExtended.find((w) => !w.zoomPreviewUrl)) {
      this.onBuildComplete('data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==', false);
      return;
    }

    const dpiX = canvasWidth / previewWidth;
    const dpiY = canvasHeight / previewHeight;
    const dpi = Math.min(dpiX, dpiY);
    const shiftX = (canvasWidth - previewWidth * dpi) / 2;
    const shiftY = (canvasHeight - previewHeight * dpi) / 2;

    const ctx = this.canvas.current.getContext('2d');

    ctx.beginPath();
    fillBackrround(ctx, currentColor, canvasWidth, canvasHeight);
    if (onlyPlaceHolders) {
      ctx.strokeStyle = 'black';
      ctx.fillStyle = 'black';
      ctx.moveTo(0, canvasHeight - 3);
      ctx.lineTo(canvasWidth, canvasHeight - 3);
      ctx.stroke();
      ctx.textBaseline = 'bottom';
      ctx.textAlign = 'left';
      ctx.font = 'bold 12px sans-serif';
      ctx.fillText('Floor', 3, canvasHeight - 3);
    }
    ctx.closePath();
    this.renderWallPaper(shiftX, shiftY, dpi, ctx, 0, worksExtended.length);
  }

  render() {
    const { onClose, display } = this.props;
    const { width, height } = Constants.preview;
    const { previewSrc } = this.state;
    let preview = null;
    if (display) {
      preview = previewSrc ? (
        <img src={ previewSrc } alt="preview" />
      ) : (
        <Loader active inline>Building preview, please hold on.</Loader>
      );
    }

    const editOverLay = onClose ? (
      <div className={ classes.editOverlay }>
        <Button
          className={ classes.action }
          color="black"
          onClick={ onClose }
        >
          GO BACK TO WALL TO EDIT PREVIEW
        </Button>
      </div>
    ) : null;

    return (
      <div className={ display ? classes.previewSection : null }>
        <div>
          { preview }
        </div>
        <canvas
          ref={ this.canvas }
          width={ width }
          height={ height }
          className={ classes.canvas }
        />
        { editOverLay }
      </div>
    );
  }
}
